// PoolScreener data — DefiLlama yields client. Primary universe source: one
// call gives TVL, volume, and the fee/reward APY split (Aerodrome emissions
// arrive as apyReward). https://yields.llama.fi/pools
import { z } from 'zod';
import { fetchJson } from './http';
import { cached } from './cache';
import type { PoolUniverseItem, ScreenerProject } from './types';

const PROJECTS: ReadonlySet<string> = new Set(['uniswap-v3', 'aerodrome-slipstream']);

const DefillamaPoolSchema = z.object({
  chain: z.string(),
  project: z.string(),
  symbol: z.string(),
  tvlUsd: z.number(),
  pool: z.string(),
  apy: z.number().nullable().optional(),
  apyBase: z.number().nullable().optional(),
  apyReward: z.number().nullable().optional(),
  volumeUsd1d: z.number().nullable().optional(),
  underlyingTokens: z.array(z.string()).nullable().optional(),
});

export const DefillamaResponseSchema = z.object({
  status: z.string(),
  data: z.array(DefillamaPoolSchema),
});
export type DefillamaResponse = z.infer<typeof DefillamaResponseSchema>;

export interface UniverseOpts {
  /** Minimum pool TVL to include. Default $2M (the config-panel default). */
  tvlFloorUsd?: number;
  /** Restrict to these chains (DefiLlama chain names, e.g. "Base"). */
  chains?: string[];
}

/** Pure: filter DefiLlama pools to our venues + TVL floor and normalize.
 *  Exposed for unit tests (recorded JSON, no network). */
export function mapPoolUniverse(
  resp: DefillamaResponse,
  opts: UniverseOpts = {},
): PoolUniverseItem[] {
  const floor = opts.tvlFloorUsd ?? 2_000_000;
  const chains = opts.chains && opts.chains.length > 0 ? new Set(opts.chains) : null;
  return resp.data
    .filter(
      (p) =>
        PROJECTS.has(p.project) &&
        p.tvlUsd >= floor &&
        (chains === null || chains.has(p.chain)),
    )
    .map((p) => ({
      poolId: p.pool,
      chain: p.chain,
      project: p.project as ScreenerProject,
      symbol: p.symbol,
      tvlUsd: p.tvlUsd,
      volumeUsd1d: p.volumeUsd1d ?? null,
      apy: p.apy ?? null,
      apyBase: p.apyBase ?? null,
      apyReward: p.apyReward ?? null,
      underlyingTokens: p.underlyingTokens ?? null,
    }))
    .sort((a, b) => b.tvlUsd - a.tvlUsd);
}

/** Fetch + normalize the pool universe (cached 1h). */
export async function fetchPoolUniverse(opts: UniverseOpts = {}): Promise<PoolUniverseItem[]> {
  const resp = await cached('defillama:pools', () =>
    fetchJson('https://yields.llama.fi/pools', DefillamaResponseSchema),
  );
  return mapPoolUniverse(resp, opts);
}
